import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  FaFlask, FaSync, FaUpload, FaDownload, FaEye, FaSave,
  FaCheckCircle, FaExclamationTriangle, FaFilePdf, FaFileWord, FaFile,
  FaEdit, FaTrashAlt, FaPlus, FaTimes, FaChevronDown, FaChevronRight,
  FaFileExcel, FaPaperPlane, FaCheck, FaUndo,
} from 'react-icons/fa';
import { cleaningConfigAPI, detergentMasterAPI, acceptanceCriteriaAPI, commercialConfigAPI } from '../services/api';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import { stampPdfFooter } from '../utils/reportProfiles';
import useApprovalRights from '../hooks/useApprovalRights';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import { ActionsMenu } from '../components/ActionsMenu';
import { Modal, ModalFooter } from '../components/Modals';
import StatusBadge from '../components/StatusBadge';

const API_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : 'http://localhost:5000';

const CLEANING_METHODS = ['Manual', 'CIP', 'COP', 'Automated'];
const RINSE_UNITS = ['L', 'mL'];
// Where a part is cleaned can differ from where the machine runs — e.g. a filling
// machine is cleaned in situ in Grade A while its change parts are washed outside
// the aseptic area in the unit preparation room.
const CLEANING_AREA_GRADES = ['A', 'B', 'C', 'D', 'CNC', 'Unclassified'];
const CLEANING_LOCATION_SUGGESTIONS = [
  'In situ (at equipment location)',
  'Unit preparation room',
  'Washing area',
  'Change part washing room',
  'Outside aseptic area',
];
const CleaningLocationOptions = () => (
  <datalist id="cleaning-location-options">
    {CLEANING_LOCATION_SUGGESTIONS.map(l => <option key={l} value={l} />)}
  </datalist>
);


const getUser = () => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } };

const FileIcon = ({ name }) => {
  if (!name) return <FaFile />;
  const ext = name.split('.').pop().toLowerCase();
  if (ext === 'pdf') return <FaFilePdf style={{ color: '#dc2626' }} />;
  if (['doc', 'docx'].includes(ext)) return <FaFileWord style={{ color: '#2563eb' }} />;
  return <FaFile />;
};

const rowKey = (eq) =>
  eq.part_id ? `${eq.equipment_id}_${eq.part_id}` : `${eq.equipment_id}_null`;

function mergeRows(equipmentList, savedConfigs) {
  const savedMap = {};
  savedConfigs.forEach(c => {
    const k = c.part_id ? `${c.equipment_id}_${c.part_id}` : `${c.equipment_id}_null`;
    savedMap[k] = c;
  });
  return equipmentList.map(eq => {
    const k = rowKey(eq);
    const saved = savedMap[k];
    return {
      id: saved?.id ?? null,
      equipment_id:     eq.equipment_id,
      equipment_code:   eq.equipment_code || '',
      equipment_name:   eq.equipment_name || '',
      plant_code:       saved?.plant_code || eq.unit_plant || '',
      part_id:          eq.part_id ?? null,
      part_name:        eq.part_name || '',
      material_of_construction: eq.material_of_construction || '',
      surface_area:     eq.surface_area ?? '',
      surface_area_unit:eq.surface_area_unit || 'cm²',
      line_id:          eq.line_id ?? null,
      cleaning_method:  saved?.cleaning_method ?? eq.default_cleaning_method ?? '',
      cleaning_location:   saved?.cleaning_location   ?? '',
      cleaning_area_grade: saved?.cleaning_area_grade ?? '',
      rinsing_agent:    saved?.rinsing_agent   ?? '',
      rinse_volume:     saved?.rinse_volume    ?? '',
      rinse_volume_unit:saved?.rinse_volume_unit ?? 'L',
      detergent_used:   saved?.detergent_used  ?? false,
      detergent_name:   saved?.detergent_name  ?? '',
      detergent_master_id: saved?.detergent_master_id ?? null,
      remarks:          saved?.remarks         ?? '',
      sop_attachment:    saved?.sop_attachment    ?? null,
      sop_original_name: saved?.sop_original_name ?? null,
      status:            saved?.status ?? 'Draft',
      submitted_by:      saved?.submitted_by ?? null,
      submitted_at:      saved?.submitted_at ?? null,
      submitted_name:    saved?.submitted_name ?? '',
      approved_by:       saved?.approved_by ?? null,
      approved_at:       saved?.approved_at ?? null,
      approved_name:     saved?.approved_name ?? '',
      updated_by_display:saved?.updated_by_display ?? '',
      updated_at:        saved?.updated_at ?? null,
      _dirty: false,
      _saved: !!saved,
    };
  });
}

// ── Addable Dropdown ──────────────────────────────────────────────────────────
const AddableDropdown = ({ value, options, placeholder, onChange, onAddNew, disabled, labelKey = 'name' }) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const positionMenu = () => {
    const anchor = ref.current?.getBoundingClientRect();
    if (!anchor) return;
    const maxWidth = Math.max(160, window.innerWidth - 16);
    const width = Math.min(Math.max(anchor.width, 220), maxWidth);
    const visibleRows = Math.min(filtered.length + (value ? 1 : 0), 5);
    const height = Math.min(260, Math.min(window.innerHeight - 16, Math.max(130, 58 + visibleRows * 34 + 42)));
    const left = Math.min(Math.max(8, anchor.left), window.innerWidth - width - 8);
    const below = anchor.bottom + 4;
    const top = below + height <= window.innerHeight - 8
      ? below
      : Math.max(8, anchor.top - height - 4);
    setMenuPosition({ top, left, width, height: Math.min(height, window.innerHeight - top - 8) });
  };

  useEffect(() => {
    const h = (e) => {
      if (!ref.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    positionMenu();
    const reposition = () => positionMenu();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const filtered = options.filter(o =>
    String(o[labelKey] || o).toLowerCase().includes(search.toLowerCase())
  );
  const label = (o) => String(o[labelKey] || o);

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: '130px' }}>
      <div
        onClick={() => {
          if (disabled) return;
          if (open) setOpen(false);
          else { setSearch(''); positionMenu(); setOpen(true); }
        }}
        style={{
          border: '1px solid var(--border-color)', borderRadius: '4px',
          padding: '3px 7px', fontSize: 'var(--fs-md)', cursor: disabled ? 'default' : 'pointer',
          background: disabled ? 'var(--surface-light)' : 'var(--card-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px',
          minHeight: '28px',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                       color: value ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
          {value || placeholder || 'Select…'}
        </span>
        {!disabled && <FaChevronDown style={{ fontSize: 'var(--fs-2xs)', flexShrink: 0 }} />}
      </div>

      {open && menuPosition && createPortal(
        <div ref={menuRef} style={{
          position: 'fixed', top: menuPosition.top, left: menuPosition.left, zIndex: 2147483000,
          width: menuPosition.width, minWidth: '200px',
          background: 'var(--card-bg)', border: '1px solid var(--border-color)',
          borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          height: menuPosition.height, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ padding: '5px 7px', borderBottom: '1px solid var(--border-color)' }}>
            <input autoFocus type="text" className="form-control"
              style={{ fontSize: 'var(--fs-md)', padding: '3px 6px' }} placeholder="Search…"
              value={search} onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()} />
          </div>
          <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
            {value && (
              <div onClick={() => { onChange(''); setSearch(''); setOpen(false); }}
                style={{ padding: '5px 9px', fontSize: 'var(--fs-md)', cursor: 'pointer',
                         color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                — Clear —
              </div>
            )}
            {filtered.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>No match</div>
            )}
            {filtered.map((o, i) => (
              <div key={i} onClick={() => { onChange(label(o)); setSearch(''); setOpen(false); }}
                style={{ padding: '5px 9px', fontSize: 'var(--fs-md)', cursor: 'pointer',
                         background: label(o) === value ? 'color-mix(in srgb, var(--primary-color) 12%, var(--card-bg))' : 'transparent' }}>
                {label(o)}
              </div>
            ))}
          </div>
          <div onClick={() => { onAddNew(search || ''); setOpen(false); setSearch(''); }}
            style={{ padding: '5px 9px', fontSize: 'var(--fs-md)', cursor: 'pointer',
                     borderTop: '1px solid var(--border-color)', color: 'var(--primary-color)',
                     display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaPlus style={{ fontSize: 'var(--fs-xs)' }} /> Add "{search || 'new'}"
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ── E-Sign Modal ──────────────────────────────────────────────────────────────
const ESignModal = ({ title, action, label, onClose, onConfirm }) => {
  const [password, setPassword] = useState('');
  const [comment, setComment]   = useState('');
  const [loading, setLoading]   = useState(false);

  const submit = async () => {
    if (!password.trim()) { toast.error('Password is required'); return; }
    setLoading(true);
    try {
      await onConfirm(password, comment);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen size="sm" zIndex={1300} title={title} onClose={onClose} resizable={false} maximizable={false} minimizable={false}>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '3px', display: 'block' }}>
              Password (Electronic Signature) *
            </label>
            <input type="password" className="form-control" value={password}
              onChange={e => setPassword(e.target.value)} autoFocus
              onKeyDown={e => e.key === 'Enter' && submit()} />
          </div>
          <div>
            <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '3px', display: 'block' }}>Comment</label>
            <textarea className="form-control" rows={2} value={comment}
              onChange={e => setComment(e.target.value)} style={{ resize: 'vertical' }} />
          </div>
        </div>
        <ModalFooter>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className={`btn btn-${action === 'approve' ? 'success' : action === 'delete' || action === 'returnDraft' ? 'warning' : 'primary'}`}
            onClick={submit} disabled={loading}>
            {loading ? 'Processing…' : label}
          </button>
        </ModalFooter>
    </Modal>
  );
};

// ── Row Edit Modal ────────────────────────────────────────────────────────────
const EditModal = ({ row, detergents, solvents, onClose, onSave, onAddDetergent, onAddSolvent, canEdit }) => {
  const [form, setForm] = useState({ ...row });
  const set = (f, v) => setForm(prev => ({
    ...prev, [f]: v,
    ...(f === 'detergent_used' && !v ? { detergent_name: '', detergent_master_id: null } : {}),
    ...(f === 'detergent_name' ? { detergent_master_id: detergents.find(d => d.detergent_name === v)?.id ?? null } : {}),
  }));

  return (
    <Modal isOpen size="md" title={`${canEdit ? 'Edit' : 'View'} — ${form.equipment_name}${form.part_name ? ` / ${form.part_name}` : ''}`} onClose={onClose}>

        {/* Status badge */}
        {form.status && (
          <div style={{ marginBottom: '16px' }}>
            <StatusBadge status={form.status} />
          </div>
        )}

        <div style={{ display: 'grid', gap: '14px' }}>
          <div>
            <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Cleaning Method</label>
            <select className="form-control" value={form.cleaning_method || ''} onChange={e => set('cleaning_method', e.target.value)} disabled={!canEdit}>
              <option value="">— Select —</option>
              {CLEANING_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '8px' }}>
            <div>
              <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Cleaning Location</label>
              <input className="form-control" list="cleaning-location-options" value={form.cleaning_location || ''}
                onChange={e => set('cleaning_location', e.target.value)} disabled={!canEdit}
                placeholder="e.g. In situ, Unit preparation room" />
            </div>
            <div>
              <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Area Grade</label>
              <select className="form-control" value={form.cleaning_area_grade || ''} onChange={e => set('cleaning_area_grade', e.target.value)} disabled={!canEdit}>
                <option value="">— Select —</option>
                {CLEANING_AREA_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '-6px' }}>
            Expand the equipment (▶) to set a different location per sub part — e.g. machine cleaned in situ in Grade A, change parts washed outside the aseptic area in the unit preparation room.
          </div>
          <div>
            <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Rinsing Agent</label>
            {canEdit ? (
              <AddableDropdown
                value={form.rinsing_agent || ''}
                options={solvents}
                labelKey="solvent_name"
                placeholder="Select rinsing agent…"
                onChange={v => set('rinsing_agent', v)}
                onAddNew={name => onAddSolvent(name, v => set('rinsing_agent', v))}
              />
            ) : (
              <input className="form-control" value={form.rinsing_agent || ''} readOnly />
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '8px' }}>
            <div>
              <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Rinse Volume</label>
              <input className="form-control" type="number" min="0" value={form.rinse_volume || ''}
                onChange={e => set('rinse_volume', e.target.value)} placeholder="e.g. 10" disabled={!canEdit} />
            </div>
            <div>
              <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Unit</label>
              <select className="form-control" value={form.rinse_volume_unit || 'L'} onChange={e => set('rinse_volume_unit', e.target.value)} disabled={!canEdit}>
                {RINSE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--info-text)', marginTop: '-4px', marginBottom: '8px', padding: '8px 10px', borderRadius: '6px', background: 'var(--info-bg)', border: '1px solid var(--info-border)' }}>
            This rinse volume is a record of the actual cleaning procedure and is not read by the MACO rinse-limit calculation — Sampling Plans takes its own rinse volume input per plan. If this value changes, update it on the relevant Sampling Plan(s) too.
          </div>
          <div>
            <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {canEdit ? (
                <input type="checkbox" checked={!!form.detergent_used} onChange={e => set('detergent_used', e.target.checked)} style={{ width: '15px', height: '15px' }} />
              ) : (
                form.detergent_used ? <FaCheckCircle style={{ color: 'var(--success-color)' }} /> : null
              )}
              Detergent Used
            </label>
            {form.detergent_used && (
              <>
                <AddableDropdown
                  value={form.detergent_name || ''}
                  options={detergents}
                  labelKey="detergent_name"
                  placeholder="Select detergent…"
                  onChange={v => set('detergent_name', v)}
                  onAddNew={name => onAddDetergent(name, v => set('detergent_name', v))}
                  disabled={!canEdit}
                />
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--info-text)', marginTop: '6px', padding: '8px 10px', borderRadius: '6px', background: 'var(--info-bg)', border: '1px solid var(--info-border)' }}>
                  Once this config is Approved, the Detergent MACO criterion (if enabled) uses this specific detergent's LD50 from the Detergent Master list (if set there) for this train — falling back to the global LD50/body-weight/safety-factor in <Link to="/maco" style={{ fontWeight: 600 }}>MACO → Settings</Link> only if no LD50 is recorded for this detergent. If multiple equipment on a train have different Approved detergents, the most recently approved one is used.
                </div>
              </>
            )}
          </div>
          <div>
            <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Remarks</label>
            <textarea className="form-control" rows={2} value={form.remarks || ''}
              onChange={e => set('remarks', e.target.value)} disabled={!canEdit} style={{ resize: 'vertical' }} />
          </div>
        </div>

        {/* Audit info */}
        {form._saved && (
          <div style={{ marginTop: '14px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', borderTop: '1px solid var(--border-color)', paddingTop: '10px', display: 'grid', gap: '4px' }}>
            {form.updated_by_display && <span>Last updated by: <strong>{form.updated_by_display}</strong>{form.updated_at ? ` on ${new Date(form.updated_at).toLocaleString()}` : ''}</span>}
            {form.submitted_name && <span>Submitted by: <strong>{form.submitted_name}</strong>{form.submitted_at ? ` on ${new Date(form.submitted_at).toLocaleString()}` : ''}</span>}
            {form.approved_name && <span>Approved by: <strong>{form.approved_name}</strong>{form.approved_at ? ` on ${new Date(form.approved_at).toLocaleString()}` : ''}</span>}
          </div>
        )}

        <ModalFooter>
          <button className="btn btn-outline" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => onSave(form)}>
              <FaSave /> Save
            </button>
          )}
        </ModalFooter>
    </Modal>
  );
};

// ── Add-New modal (generic: detergent or solvent) ────────────────────────────
const AddMasterModal = ({ title, nameLabel, namePlaceholder, extraFields, onClose, onSaved }) => {
  const [fields, setFields] = useState({ name: '', ...Object.fromEntries(extraFields.map(f => [f.key, ''])) });
  const [saving, setSaving] = useState(false);

  return (
    <Modal isOpen size="sm" zIndex={1300} title={title} onClose={onClose} resizable={false} maximizable={false} minimizable={false}>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '3px', display: 'block' }}>{nameLabel} *</label>
            <input className="form-control" autoFocus value={fields.name}
              onChange={e => setFields(f => ({ ...f, name: e.target.value }))} placeholder={namePlaceholder} />
          </div>
          {extraFields.map(ef => (
            <div key={ef.key}>
              <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '3px', display: 'block' }}>{ef.label}</label>
              <input className="form-control" value={fields[ef.key]}
                onChange={e => setFields(f => ({ ...f, [ef.key]: e.target.value }))} placeholder={ef.placeholder || ''} />
            </div>
          ))}
        </div>
        <ModalFooter>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving}
            onClick={async () => {
              if (!fields.name.trim()) { toast.error(`${nameLabel} is required`); return; }
              setSaving(true);
              try { await onSaved(fields); onClose(); }
              catch (e) { toast.error(e?.response?.data?.message || 'Failed to save'); }
              finally { setSaving(false); }
            }}>
            {saving ? 'Saving…' : 'Add'}
          </button>
        </ModalFooter>
    </Modal>
  );
};


// ── Main Component ─────────────────────────────────────────────────────────────
const CleaningConfig = () => {
  const user    = getUser();
  const role    = String(user?.role || '').toLowerCase();
  const canEdit = ['super admin', 'admin', 'qa'].includes(role);
  const isAdmin = ['super admin', 'admin'].includes(role);
  const { canFinal: canApproveConfig } = useApprovalRights('cleaning_config.approval');

  const activeLine  = localStorage.getItem('activeLine')  || '';
  const activePlant = localStorage.getItem('activePlant') || '';

  const [rows,       setRows]       = useState([]);
  const [detergents, setDetergents] = useState([]);
  const [solvents,   setSolvents]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState({});
  const [uploading,  setUploading]  = useState({});
  const [savingAll,  setSavingAll]  = useState(false);

  // Grouped vs expanded per equipment (default: grouped = true)
  const [grouped, setGrouped] = useState({});   // { eqId: true } = grouped, false = expanded

  // Modals
  const [editModal,    setEditModal]    = useState(null);  // {idx, mode}
  const [eSignModal,   setESignModal]   = useState(null);  // {idx, action, title, label}
  const [qmsLinked, setQmsLinked] = useState(false);
  const [changeRefGate, setChangeRefGate] = useState(null); // {idx} while the gate is open for an Approved row
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  const [pendingChangeRef, setPendingChangeRef] = useState(null);

  useEffect(() => {
    commercialConfigAPI.getRuntime().then((res) => {
      const integrations = res?.data?.data?.integrations || {};
      setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
    }).catch(() => {});
  }, []);
  const [addDetModal,  setAddDetModal]  = useState(null);  // {initial, callback}
  const [addSolvModal, setAddSolvModal] = useState(null);  // {initial, callback}

  const fileInputRefs = useRef({});
  const importRef     = useRef(null);
  const mobileImportInputRef = useRef(null);

  // ── load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeLine && !['all', 'all lines', 'all sections'].includes(activeLine.trim().toLowerCase())) params.line_id = activeLine;
      if (activePlant && !['all', 'all units'].includes(activePlant.trim().toLowerCase())) params.plant_code = activePlant;

      const [eqRes, cfgRes, detRes, solvRes] = await Promise.all([
        cleaningConfigAPI.getEquipment(params),
        cleaningConfigAPI.getAll(params),
        detergentMasterAPI.getAll(),
        acceptanceCriteriaAPI.getSolvents({ plant_code: activePlant || '' }).catch(() => ({ data: { data: [] } })),
      ]);

      setRows(mergeRows(eqRes?.data?.data || [], cfgRes?.data?.data || []));
      setDetergents(detRes?.data?.data || []);
      setSolvents(solvRes?.data?.data || []);
    } catch {
      toast.error('Failed to load cleaning configuration data.');
    } finally {
      setLoading(false);
    }
  }, [activeLine, activePlant]);

  useEffect(() => { load(); }, [load]);

  // ── helpers ────────────────────────────────────────────────────────────────
  const buildPayload = (row) => ({
    equipment_id:     row.equipment_id,
    part_id:          row.part_id,
    line_id:          row.line_id || activeLine || null,
    plant_code:       row.plant_code || (activePlant && !['All', 'All Units'].includes(activePlant) ? activePlant : null),
    equipment_name:   row.equipment_name,
    equipment_code:   row.equipment_code,
    part_name:        row.part_name,
    cleaning_method:  row.cleaning_method,
    cleaning_location:   row.cleaning_location || null,
    cleaning_area_grade: row.cleaning_area_grade || null,
    rinsing_agent:    row.rinsing_agent,
    rinse_volume:     row.rinse_volume || null,
    rinse_volume_unit:row.rinse_volume_unit || 'L',
    detergent_used:   !!row.detergent_used,
    detergent_name:   row.detergent_used ? row.detergent_name : '',
    detergent_master_id: row.detergent_used ? (row.detergent_master_id || null) : null,
    remarks:          row.remarks,
  });

  // ── update a single row field ──────────────────────────────────────────────
  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = next[idx];
      next[idx] = { ...row, [field]: value, _dirty: true };
      if (field === 'detergent_used' && !value) { next[idx].detergent_name = ''; next[idx].detergent_master_id = null; }
      if (field === 'detergent_name') next[idx].detergent_master_id = detergents.find(d => d.detergent_name === value)?.id ?? null;

      // If this equipment is grouped, propagate cleaning_method/rinsing_agent/rinse_volume to all parts
      const eqId = row.equipment_id;
      const isGrouped = grouped[eqId] !== false;
      if (isGrouped && ['cleaning_method', 'cleaning_location', 'cleaning_area_grade', 'rinsing_agent', 'rinse_volume', 'rinse_volume_unit', 'detergent_used', 'detergent_name'].includes(field)) {
        for (let i = 0; i < next.length; i++) {
          if (next[i].equipment_id === eqId && i !== idx) {
            next[i] = { ...next[i], [field]: value, _dirty: true };
            if (field === 'detergent_used' && !value) { next[i].detergent_name = ''; next[i].detergent_master_id = null; }
            if (field === 'detergent_name') next[i].detergent_master_id = detergents.find(d => d.detergent_name === value)?.id ?? null;
          }
        }
      }
      return next;
    });
  };

  // ── save one row ───────────────────────────────────────────────────────────
  const saveRow = async (idx) => {
    const row = rows[idx];
    const k   = rowKey(row);
    setSaving(s => ({ ...s, [k]: true }));
    try {
      let saved;
      if (row.id) {
        saved = (await cleaningConfigAPI.update(row.id, buildPayload(row)))?.data?.data;
      } else {
        saved = (await cleaningConfigAPI.create(buildPayload(row)))?.data?.data;
      }
      setRows(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved, id: saved?.id ?? next[idx].id, _dirty: false, _saved: true };
        return next;
      });
      toast.success(`${row.equipment_name}${row.part_name ? ' / ' + row.part_name : ''} saved.`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Save failed.');
      return false;
    } finally {
      setSaving(s => { const n = { ...s }; delete n[k]; return n; });
    }
  };

  // ── save all dirty ─────────────────────────────────────────────────────────
  const saveAll = async () => {
    const dirtyIdxs = rows.map((r, i) => r._dirty ? i : -1).filter(i => i >= 0);
    if (!dirtyIdxs.length) { toast.info('No unsaved changes.'); return; }
    setSavingAll(true);
    let ok = 0, fail = 0;
    for (const idx of dirtyIdxs) {
      const success = await saveRow(idx);
      if (success) ok++; else fail++;
    }
    setSavingAll(false);
    if (fail) toast.error(`${fail} row(s) failed.`);
    else toast.success(`${ok} row(s) saved.`);
  };

  // ── save from modal ────────────────────────────────────────────────────────
  const saveFromModal = async (updatedRow) => {
    const idx = editModal.idx;
    if (rows[idx]?.id && JSON.stringify(updatedRow) === JSON.stringify(rows[idx]) && !rows[idx]._dirty) {
      toast.info('No changes were made — nothing to save.');
      setEditModal(null);
      return;
    }
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...updatedRow, _dirty: true };
      return next;
    });
    setEditModal(null);
    await saveRow(idx);
  };

  // ── SOP upload (auto-save first if no id) ─────────────────────────────────
  const handleSopUpload = async (idx, file) => {
    let row = rows[idx];
    if (!row.id) {
      toast.info('Saving row first…');
      const ok = await saveRow(idx);
      if (!ok) return;
      row = rows[idx]; // re-read after save — but state may not have updated yet
      // pull fresh id from state
      row = { ...rows[idx] };
      if (!row.id) { toast.error('Could not save row. Please try again.'); return; }
    }
    const k = rowKey(row);
    setUploading(u => ({ ...u, [k]: true }));
    try {
      const fd = new FormData();
      fd.append('sop_file', file);
      const res = await cleaningConfigAPI.uploadSop(row.id, fd);
      const updated = res?.data?.data;
      setRows(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx],
          sop_attachment:    updated?.sop_attachment    ?? next[idx].sop_attachment,
          sop_original_name: updated?.sop_original_name ?? file.name,
        };
        return next;
      });
      toast.success('SOP uploaded.');
    } catch { toast.error('SOP upload failed.'); }
    finally { setUploading(u => { const n = { ...u }; delete n[k]; return n; }); }
  };

  // ── e-sign actions ─────────────────────────────────────────────────────────
  const openESign = (idx, action) => {
    const labels = {
      submit:      { title: 'Submit for Approval', action: 'submit', label: 'Confirm Submit' },
      approve:     { title: 'Approve Record',      action: 'approve', label: 'Confirm Approve' },
      returnDraft: { title: 'Return to Draft',     action: 'returnDraft', label: 'Confirm Return' },
    };
    if (action === 'returnDraft' && rows[idx]?.status === 'Approved') {
      setPendingChangeRef(null);
      setChangeRefGate({ idx });
      return;
    }
    setESignModal({ idx, ...labels[action] });
  };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      setESignModal({ idx: changeRefGate.idx, title: 'Return to Draft', action: 'returnDraft', label: 'Confirm Return' });
      setChangeRefGate(null);
    } finally {
      setChangeRefBusy(false);
    }
  };

  const handleESign = async (password, comment) => {
    const { idx, action } = eSignModal;
    const row = rows[idx];
    let res;
    if (action === 'submit')      res = await cleaningConfigAPI.submit(row.id, { password, comment });
    if (action === 'approve')     res = await cleaningConfigAPI.approve(row.id, { password, comment });
    if (action === 'returnDraft') {
      const body = { password };
      if (row.status === 'Approved' && pendingChangeRef) {
        body.change_reference_type = pendingChangeRef.reference_type;
        body.change_reference_number = pendingChangeRef.reference_number;
        body.change_reference_files = pendingChangeRef.reference_files || [];
      }
      res = await cleaningConfigAPI.returnDraft(row.id, body);
      setPendingChangeRef(null);
    }
    const updated = res?.data?.data;
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...updated };
      return next;
    });
    toast.success(action === 'approve' ? 'Approved.' : action === 'returnDraft' ? 'Returned to Draft.' : 'Submitted.');
    await load();
  };

  // ── delete row ─────────────────────────────────────────────────────────────
  const deleteRow = async (idx) => {
    const row = rows[idx];
    if (row.status === 'Approved') { toast.error('Cannot deactivate an approved record.'); return; }
    if (!window.confirm(`Deactivate config for "${row.equipment_name}${row.part_name ? ' / ' + row.part_name : ''}"?`)) return;
    if (row.id) {
      try { await cleaningConfigAPI.delete(row.id); } catch { toast.error('Deactivate failed.'); return; }
    }
    setRows(prev => prev.map((r, i) => i === idx ? {
      ...r, id: null, _saved: false, _dirty: false,
      cleaning_method: '', cleaning_location: '', cleaning_area_grade: '', rinsing_agent: '', rinse_volume: '', rinse_volume_unit: 'L',
      detergent_used: false, detergent_name: '', remarks: '', status: 'Draft',
      sop_attachment: null, sop_original_name: null,
    } : r));
    toast.success('Cleared.');
    await load();
  };

  // ── detergent inline add ───────────────────────────────────────────────────
  const handleAddDetergent = (initial, callback) => setAddDetModal({ initial, callback });
  const handleAddSolvent   = (initial, callback) => setAddSolvModal({ initial, callback });

  // ── group/expand toggle ────────────────────────────────────────────────────
  const toggleGroup = (eqId) => setGrouped(prev => ({ ...prev, [eqId]: !(prev[eqId] !== false) }));

  // ── Export Excel ───────────────────────────────────────────────────────────
  const exportExcel = () => {
    const data = rows.map(r => ({
      'Equipment Name':   r.equipment_name,
      'Equipment Code':  r.equipment_code,
      'Sub Part':        r.part_name,
      'Status':          r.status || 'Draft',
      'Cleaning Method': r.cleaning_method,
      'Cleaning Location': r.cleaning_location || '',
      'Area Grade':      r.cleaning_area_grade || '',
      'Rinsing Agent':   r.rinsing_agent,
      'Rinse Volume':    r.rinse_volume,
      'Rinse Unit':      r.rinse_volume_unit,
      'Detergent Used':  r.detergent_used ? 'Yes' : 'No',
      'Detergent Name':  r.detergent_used ? r.detergent_name : '',
      'Remarks':         r.remarks,
      'Updated By':      r.updated_by_display || '',
      'SOP':             r.sop_original_name || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cleaning Config');
    XLSX.writeFile(wb, `CleaningConfig_${activeLine || 'All'}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // ── Export PDF ─────────────────────────────────────────────────────────────
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Cleaning Configuration Report', pageW / 2, 36, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Line: ${activeLine || 'All'} | Generated: ${new Date().toLocaleString()}`, pageW / 2, 52, { align: 'center' });

    const tableRows = rows.map(r => [
      r.equipment_name,
      r.part_name || '—',
      r.cleaning_method || '—',
      [r.cleaning_location, r.cleaning_area_grade ? `Grade ${r.cleaning_area_grade}` : ''].filter(Boolean).join(', ') || '—',
      r.rinsing_agent || '—',
      r.rinse_volume ? `${r.rinse_volume} ${r.rinse_volume_unit || 'L'}` : '—',
      r.detergent_used ? (r.detergent_name || 'Yes') : 'No',
      r.remarks || '—',
      r.status || 'Draft',
    ]);

    doc.autoTable({
      head: [['Equipment', 'Sub Part', 'Method', 'Cleaning Location', 'Rinsing Agent', 'Rinse Vol.', 'Detergent', 'Remarks', 'Status']],
      body: tableRows,
      startY: 64,
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 250, 245] },
      columnStyles: { 0: { cellWidth: 100 }, 1: { cellWidth: 80 }, 8: { cellWidth: 55 } },
      margin: { left: 24, right: 24 },
    });

    stampPdfFooter(doc, 'cleaning_config');
    doc.save(`CleaningConfig_${activeLine || 'All'}_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  // ── Import template ────────────────────────────────────────────────────────
  const downloadTemplate = () => {
    const headers = ['Equipment Name','Equipment Code','Sub Part','Cleaning Method','Cleaning Location','Area Grade','Rinsing Agent','Rinse Volume','Rinse Unit','Detergent Used','Detergent Name','Remarks'];
    const ws = XLSX.utils.aoa_to_sheet([headers, headers.map(() => '')]);
    ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 2, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cleaning Config');
    XLSX.writeFile(wb, 'CleaningConfig_ImportTemplate.xlsx');
  };

  // ── Import from Excel ──────────────────────────────────────────────────────
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb   = XLSX.read(evt.target.result, { type: 'binary' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        let imported = 0;
        const unmatched = [];
        setRows(prev => {
          const next = [...prev];
          data.forEach(row => {
            const eqName   = String(row['Equipment Name'] || '').trim().toLowerCase();
            const partName = String(row['Sub Part']       || '').trim().toLowerCase();
            const idx = next.findIndex(r =>
              r.equipment_name.toLowerCase() === eqName &&
              (r.part_name || '').toLowerCase() === partName
            );
            if (idx < 0) {
              // Rows here mirror Equipment Master's parts one-to-one (see mergeRows above) —
              // there's no freestanding "create" for a cleaning config row, so a name that
              // doesn't match an existing equipment/sub-part can only be a typo or an
              // equipment not yet defined there. Previously this silently vanished.
              unmatched.push(`${row['Equipment Name'] || '(blank)'} / ${row['Sub Part'] || '(blank)'}`);
              return;
            }
            next[idx] = {
              ...next[idx],
              cleaning_method:   String(row['Cleaning Method'] || '').trim() || next[idx].cleaning_method,
              cleaning_location:   String(row['Cleaning Location'] || '').trim() || next[idx].cleaning_location,
              cleaning_area_grade: String(row['Area Grade'] || '').trim() || next[idx].cleaning_area_grade,
              rinsing_agent:     String(row['Rinsing Agent']   || '').trim(),
              rinse_volume:      String(row['Rinse Volume']    || '').trim(),
              rinse_volume_unit: String(row['Rinse Unit']      || 'L').trim(),
              detergent_used:    String(row['Detergent Used']  || '').toLowerCase() === 'yes',
              detergent_name:    String(row['Detergent Name']  || '').trim(),
              remarks:           String(row['Remarks']         || '').trim(),
              _dirty: true,
            };
            imported++;
          });
          return next;
        });
        if (unmatched.length) {
          toast.warning(`${imported} row(s) imported; ${unmatched.length} skipped — no matching equipment/sub-part: ${unmatched.slice(0, 5).join('; ')}${unmatched.length > 5 ? '…' : ''}`, { autoClose: 8000 });
        } else {
          toast.success(`${imported} row(s) imported. Click Save All to persist.`);
        }
      } catch { toast.error('Failed to parse import file.'); }
    };
    reader.readAsBinaryString(file);
  };

  // ── Grouped rows view ──────────────────────────────────────────────────────
  // Group rows by equipment
  const equipmentGroups = [];
  const seenEq = {};
  rows.forEach((row, idx) => {
    const eId = row.equipment_id;
    if (!seenEq[eId]) {
      seenEq[eId] = equipmentGroups.length;
      equipmentGroups.push({ eqId: eId, equipment_name: row.equipment_name, equipment_code: row.equipment_code, rows: [] });
    }
    equipmentGroups[seenEq[eId]].rows.push({ row, idx });
  });

  const noLine = !activeLine || ['all', 'all lines', 'all sections'].includes(activeLine.trim().toLowerCase());
  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  const dirtyCount = rows.filter(r => r._dirty).length;

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title" style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaFlask /> Cleaning Configuration
            {activeLine && activeLine !== 'All' && (
              <span style={{ fontSize: 'var(--fs-base)', fontWeight: 400, color: 'var(--text-secondary)',
                             background: 'var(--surface-light)', padding: '2px 10px',
                             borderRadius: '999px', border: '1px solid var(--border-color)' }}>
                {activeLine}
              </span>
            )}
          </h2>
          <p style={{ margin: 0, fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Cleaning methods and SOPs are stored per equipment. Surface details come from Equipment Master, and rinsing-agent limits come from Acceptance Criteria.</p>
        </div>
        <div className="toolbar-full" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {canEdit && !noLine && (
            <button className="btn btn-primary" onClick={saveAll} disabled={savingAll || dirtyCount === 0}>
              <FaSave /> Save All{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
            </button>
          )}
          {!noLine && (
            <>
              <button className="btn btn-success" onClick={exportExcel} title="Export to Excel">
                <FaFileExcel /> Excel
              </button>
              <button className="btn btn-danger" onClick={exportPDF} title="Export to PDF">
                <FaFilePdf /> PDF
              </button>
            </>
          )}
          {canEdit && !noLine && (
            <button className="btn btn-outline" onClick={downloadTemplate} title="Download import template">
              <FaDownload /> Template
            </button>
          )}
          {canEdit && !noLine && (
            <label className="btn btn-info" style={{ margin: 0, cursor: 'pointer' }}>
              <FaUpload /> Import
              <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} style={{ display: 'none' }} />
            </label>
          )}
          <button className="btn btn-outline" onClick={load} title="Refresh"><FaSync /></button>
        </div>
        <div className="toolbar-compact" style={{ gap: '8px' }}>
          {canEdit && !noLine && (
            <button className="btn btn-primary" onClick={saveAll} disabled={savingAll || dirtyCount === 0}>
              <FaSave /> Save All{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
            </button>
          )}
          <ActionsMenu
            title="More actions"
            items={[
              { label: 'Excel', icon: <FaFileExcel />, show: !noLine, onClick: exportExcel },
              { label: 'PDF', icon: <FaFilePdf />, show: !noLine, onClick: exportPDF },
              { label: 'Template', icon: <FaDownload />, show: canEdit && !noLine, onClick: downloadTemplate },
              { label: 'Import', icon: <FaUpload />, show: canEdit && !noLine, onClick: () => mobileImportInputRef.current?.click() },
              { label: 'Refresh', icon: <FaSync />, onClick: load },
            ]}
          />
          <input ref={mobileImportInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} style={{ display: 'none' }} />
        </div>
      </div>

      {/* ── Guards ─────────────────────────────────────────────────────────── */}
      {noLine && (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <FaExclamationTriangle style={{ fontSize: 'var(--fs-2xl)', marginBottom: '8px', color: 'var(--warning-color)' }} />
          <p style={{ margin: 0 }}>Select a <strong>Section</strong> from the header to view cleaning configuration.</p>
        </div>
      )}
      {!noLine && rows.length === 0 && (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <FaExclamationTriangle style={{ fontSize: 'var(--fs-2xl)', marginBottom: '8px', color: 'var(--warning-color)' }} />
          <p style={{ margin: '0 0 8px' }}><strong>No active equipment is assigned to {activeLine}.</strong></p>
          <p style={{ margin: '0 0 12px', fontSize: 'var(--fs-sm)' }}>Cleaning Configuration is populated from Equipment Master. Check the equipment’s unit, production line, and active status.</p>
          <Link className="btn btn-outline btn-sm" to="/equipment-master">Open Equipment Master</Link>
        </div>
      )}

      <CleaningLocationOptions />

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      {!noLine && rows.length > 0 && (
        <FloatingScrollWrapper>
          <table style={{ minWidth: '1400px', fontSize: 'var(--fs-base)', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ minWidth: '28px', width: '28px' }}></th>
                <th style={{ minWidth: '155px' }}>Equipment Name</th>
                <th style={{ minWidth: '85px' }}>Code</th>
                <th style={{ minWidth: '120px' }}>Sub Part</th>
                <th style={{ minWidth: '115px' }}>Method</th>
                <th style={{ minWidth: '230px' }} title="Where this equipment / sub part is cleaned, and the area grade of that location">Cleaning Location / Grade</th>
                <th style={{ minWidth: '170px' }}>Cleaning SOP</th>
                <th style={{ minWidth: '135px' }}>Rinsing Agent</th>
                <th style={{ minWidth: '108px' }}>Rinse Vol.</th>
                <th style={{ minWidth: '175px' }}>Detergent</th>
                <th style={{ minWidth: '130px' }}>Remarks</th>
                <th style={{ minWidth: '70px', textAlign: 'center' }}>Status</th>
                <th style={{ minWidth: '90px', textAlign: 'center' }}>Updated By</th>
                <th style={{ minWidth: '110px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {equipmentGroups.map(({ eqId, rows: eqRows }) => {
                const isExpanded = grouped[eqId] === false; // default grouped = true
                const hasMultiple = eqRows.length > 1;
                // In grouped mode show only first row; in expanded show all
                const displayRows = isExpanded ? eqRows : [eqRows[0]];

                return displayRows.map(({ row, idx }, ri) => {
                  const k         = rowKey(row);
                  const isSaving  = !!saving[k];
                  const isUpl     = !!uploading[k];
                  const isFirst   = ri === 0;
                  const isDraft   = row.status === 'Draft' || !row.status;
                  const isSubmit  = row.status === 'Submitted';
                  const isApproved = row.status === 'Approved';
                  const solventInfo = solvents.find((solvent) => solvent.solvent_name === row.rinsing_agent);

                  return (
                    <tr key={k} style={{
                      background: row._dirty
                        ? 'color-mix(in srgb, var(--warning-color) 6%, var(--card-bg))'
                        : isApproved ? 'color-mix(in srgb, var(--success-color) 4%, var(--card-bg))'
                        : isSubmit ? 'color-mix(in srgb, var(--info-color) 4%, var(--card-bg))'
                        : row._saved ? 'color-mix(in srgb, var(--success-color) 3%, var(--card-bg))'
                        : 'var(--card-bg)',
                      borderLeft: row._dirty ? '3px solid var(--warning-color)'
                        : isApproved ? '3px solid var(--success-color)'
                        : isSubmit ? '3px solid var(--info-color)'
                        : '3px solid transparent',
                      borderBottom: '1px solid var(--border-color)',
                    }}>

                      {/* Expand toggle */}
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        {isFirst && hasMultiple && (
                          <button onClick={() => toggleGroup(eqId)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer',
                                     color: 'var(--text-secondary)', padding: '2px' }}
                            title={isExpanded ? 'Group parts' : 'Expand to per-part settings'}>
                            {isExpanded
                              ? <FaChevronDown style={{ fontSize: 'var(--fs-xs)' }} />
                              : <FaChevronRight style={{ fontSize: 'var(--fs-xs)' }} />}
                          </button>
                        )}
                      </td>

                      {/* Equipment Name */}
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                        {isFirst ? row.equipment_name : ''}
                        {isFirst && !isExpanded && hasMultiple && (
                          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', fontWeight: 400,
                                         marginLeft: '5px' }}>
                            ({eqRows.length} parts)
                          </span>
                        )}
                      </td>

                      {/* Code */}
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 'var(--fs-sm)' }}>
                        {isFirst ? (
                          row.equipment_code ? (
                            <Link
                              to="/equipment-master"
                              state={{ openViewEquipmentId: row.equipment_id }}
                              className="record-link"
                              title="View equipment"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {row.equipment_code}
                            </Link>
                          ) : `#${row.equipment_id}`
                        ) : ''}
                      </td>

                      {/* Sub Part */}
                      <td style={{ padding: '6px 8px', color: row.part_name ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>
                        {isExpanded ? (row.part_name || <em>—</em>) : (
                          !isExpanded && hasMultiple ? <em style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>All parts</em>
                          : (row.part_name || <em>—</em>)
                        )}
                        {(!hasMultiple || isExpanded) && (row.material_of_construction || row.surface_area) && (
                          <div title="Surface material and area shared with MACO, Acceptance Criteria and Sampling Plans" style={{ marginTop: '3px', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', fontWeight: 400 }}>
                            {row.material_of_construction}{row.material_of_construction && row.surface_area ? ' · ' : ''}
                            {row.surface_area ? `${row.surface_area} ${row.surface_area_unit || 'cm²'}` : ''}
                          </div>
                        )}
                      </td>

                      {/* Cleaning Method */}
                      <td style={{ padding: '4px 5px' }}>
                        <select className="form-control" style={{ fontSize: 'var(--fs-md)', padding: '3px 4px' }}
                          value={row.cleaning_method || ''} disabled={!canEdit || isApproved}
                          onChange={e => updateRow(idx, 'cleaning_method', e.target.value)}>
                          <option value="">— Select —</option>
                          {CLEANING_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </td>

                      {/* Cleaning Location / Area Grade */}
                      <td style={{ padding: '4px 5px' }}>
                        <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                          <input type="text" className="form-control" list="cleaning-location-options"
                            style={{ fontSize: 'var(--fs-md)', padding: '3px 5px', minWidth: 0, flex: 1 }}
                            placeholder="e.g. In situ / Unit prep room" value={row.cleaning_location || ''}
                            onChange={e => updateRow(idx, 'cleaning_location', e.target.value)}
                            disabled={!canEdit || isApproved} />
                          <select className="form-control" title="Area grade"
                            style={{ fontSize: 'var(--fs-md)', padding: '3px 3px', width: '72px', flex: '0 0 72px' }}
                            value={row.cleaning_area_grade || ''}
                            onChange={e => updateRow(idx, 'cleaning_area_grade', e.target.value)}
                            disabled={!canEdit || isApproved}>
                            <option value="">Grade</option>
                            {CLEANING_AREA_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                      </td>

                      {/* Cleaning SOP */}
                      <td style={{ padding: '4px 5px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                          {row.sop_attachment ? (
                            <>
                              <FileIcon name={row.sop_original_name} />
                              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', maxWidth: '70px',
                                             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={row.sop_original_name}>{row.sop_original_name}</span>
                              <a href={`${API_BASE}/uploads/${row.sop_attachment}`} target="_blank" rel="noopener noreferrer"
                                 className="btn btn-sm btn-outline" style={{ padding: '2px 5px', fontSize: 'var(--fs-sm)' }} title="View">
                                <FaEye />
                              </a>
                            </>
                          ) : (
                            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>No SOP</span>
                          )}
                          {canEdit && !isApproved && (
                            <>
                              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                     style={{ display: 'none' }}
                                     ref={el => { fileInputRefs.current[k] = el; }}
                                     onChange={e => { if (e.target.files[0]) handleSopUpload(idx, e.target.files[0]); e.target.value = ''; }} />
                              <button className="btn btn-sm btn-outline" style={{ padding: '2px 5px', fontSize: 'var(--fs-sm)' }}
                                      title={row.sop_attachment ? 'Replace SOP' : 'Upload SOP'} disabled={isUpl}
                                      onClick={() => fileInputRefs.current[k]?.click()}>
                                {isUpl ? '…' : <FaUpload />}
                              </button>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Rinsing Agent */}
                      <td style={{ padding: '4px 5px' }}>
                        {canEdit && !isApproved ? (
                          <AddableDropdown
                            value={row.rinsing_agent || ''}
                            options={solvents}
                            labelKey="solvent_name"
                            placeholder="e.g. WFI, PW"
                            onChange={v => updateRow(idx, 'rinsing_agent', v)}
                            onAddNew={name => handleAddSolvent(name, v => updateRow(idx, 'rinsing_agent', v))}
                          />
                        ) : (
                          <span style={{ fontSize: 'var(--fs-md)', padding: '0 5px', color: row.rinsing_agent ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                            {row.rinsing_agent || '—'}
                          </span>
                        )}
                        {solventInfo && <div style={{ margin: '3px 4px 0', color: 'var(--text-secondary)', fontSize: 'var(--fs-2xs)' }} title="Specification from Acceptance Criteria · Solvent Master">
                          {solventInfo.toc_limit != null ? `TOC ${solventInfo.toc_limit} ${solventInfo.toc_unit || ''}` : ''}
                          {solventInfo.conductivity_limit != null ? ` · Cond. ${solventInfo.conductivity_limit} ${solventInfo.conductivity_unit || ''}` : ''}
                          {solventInfo.ph_min != null || solventInfo.ph_max != null ? ` · pH ${solventInfo.ph_min ?? '—'}–${solventInfo.ph_max ?? '—'}` : ''}
                        </div>}
                      </td>

                      {/* Rinse Volume */}
                      <td style={{ padding: '4px 5px' }}>
                        <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                          <input type="number" min="0" className="form-control"
                            style={{ fontSize: 'var(--fs-md)', padding: '3px 4px', width: '56px' }}
                            placeholder="Vol." value={row.rinse_volume || ''}
                            onChange={e => updateRow(idx, 'rinse_volume', e.target.value)}
                            disabled={!canEdit || isApproved} />
                          <select className="form-control"
                            style={{ fontSize: 'var(--fs-md)', padding: '3px 3px', width: '44px' }}
                            value={row.rinse_volume_unit || 'L'}
                            onChange={e => updateRow(idx, 'rinse_volume_unit', e.target.value)}
                            disabled={!canEdit || isApproved}>
                            {RINSE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      </td>

                      {/* Detergent */}
                      <td style={{ padding: '4px 5px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {canEdit && !isApproved ? (
                            <input type="checkbox" checked={!!row.detergent_used}
                              onChange={e => updateRow(idx, 'detergent_used', e.target.checked)}
                              style={{ width: '14px', height: '14px', cursor: 'pointer', flexShrink: 0 }} />
                          ) : (
                            row.detergent_used
                              ? <FaCheckCircle style={{ color: 'var(--success-color)', flexShrink: 0, fontSize: 'var(--fs-base)' }} />
                              : <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>—</span>
                          )}
                          {row.detergent_used && (
                            <AddableDropdown
                              value={row.detergent_name || ''}
                              options={detergents}
                              labelKey="detergent_name"
                              placeholder="Select…"
                              onChange={v => updateRow(idx, 'detergent_name', v)}
                              onAddNew={name => handleAddDetergent(name, v => updateRow(idx, 'detergent_name', v))}
                              disabled={!canEdit || isApproved}
                            />
                          )}
                        </div>
                      </td>

                      {/* Remarks */}
                      <td style={{ padding: '4px 5px' }}>
                        <input type="text" className="form-control" style={{ fontSize: 'var(--fs-md)', padding: '3px 5px' }}
                          placeholder="Optional" value={row.remarks || ''}
                          onChange={e => updateRow(idx, 'remarks', e.target.value)}
                          disabled={!canEdit || isApproved} />
                      </td>

                      {/* Status */}
                      <td style={{ padding: '4px 5px', textAlign: 'center' }}>
                        <StatusBadge status={row.status} />
                      </td>

                      {/* Updated By */}
                      <td style={{ padding: '4px 8px', textAlign: 'center', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                        {row.updated_by_display || '—'}
                        {row.updated_at && (
                          <div style={{ fontSize: 'var(--fs-xs)' }}>
                            {new Date(row.updated_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '4px 5px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {/* View/Edit modal */}
                          <button className={`btn btn-sm ${canEdit && !isApproved ? 'btn-info' : 'btn-outline'}`}
                            style={{ padding: '3px 6px', fontSize: 'var(--fs-sm)' }}
                            onClick={() => setEditModal({ idx, mode: canEdit && !isApproved ? 'edit' : 'view' })}
                            title={canEdit && !isApproved ? 'Edit' : 'View'}>
                            {canEdit && !isApproved ? <FaEdit /> : <FaEye />}
                          </button>

                          {/* Save (inline) */}
                          {canEdit && !isApproved && (
                            <button className={`btn btn-sm ${row._dirty ? 'btn-primary' : 'btn-outline'}`}
                              style={{ padding: '3px 6px', fontSize: 'var(--fs-sm)' }}
                              onClick={() => saveRow(idx)} disabled={isSaving || !row._dirty} title="Save">
                              {isSaving ? '…' : <FaSave />}
                            </button>
                          )}

                          {/* Submit (Draft → Submitted) */}
                          {canEdit && isDraft && row._saved && !row._dirty && (
                            <button className="btn btn-sm btn-warning" style={{ padding: '3px 6px', fontSize: 'var(--fs-sm)' }}
                              onClick={() => openESign(idx, 'submit')} title="Submit for approval">
                              <FaPaperPlane />
                            </button>
                          )}

                          {/* Approve (Submitted → Approved) */}
                          {canApproveConfig && isSubmit && (
                            <button className="btn btn-sm btn-success" style={{ padding: '3px 6px', fontSize: 'var(--fs-sm)' }}
                              onClick={() => openESign(idx, 'approve')} title="Approve">
                              <FaCheck />
                            </button>
                          )}

                          <ActionsMenu items={[
                            { label: 'Return to Draft', icon: <FaUndo />, onClick: () => openESign(idx, 'returnDraft'), show: isAdmin && (isSubmit || isApproved) },
                            { label: 'Deactivate Config', icon: <FaTrashAlt />, onClick: () => deleteRow(idx), disabled: !row._saved && !row.id, show: canEdit && !isApproved, danger: true },
                          ]} />
                        </div>
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </FloatingScrollWrapper>
      )}

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      {!noLine && rows.length > 0 && (
        <div style={{ padding: '10px 16px', display: 'flex', gap: '16px', fontSize: 'var(--fs-sm)',
                      color: 'var(--text-secondary)', borderTop: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
          {[
            { color: 'var(--warning-color)', label: 'Unsaved changes' },
            { color: 'var(--success-color)', label: 'Approved' },
            { color: 'var(--info-color)', label: 'Submitted' },
          ].map(({ color, label }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: `color-mix(in srgb, ${color} 18%, var(--card-bg))`, border: `2px solid ${color}`, display: 'inline-block' }} />
              {label}
            </span>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-xs)' }}>
            ▶ = grouped (all parts share settings) | ▼ = expanded (per-part settings)
          </span>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      <ChangeReferenceModal
        open={!!changeRefGate}
        qmsLinked={qmsLinked}
        busy={changeRefBusy}
        recordLabel={changeRefGate ? `${rows[changeRefGate.idx]?.equipment_name || ''}${rows[changeRefGate.idx]?.part_name ? ' / ' + rows[changeRefGate.idx].part_name : ''}` : ''}
        onCancel={() => setChangeRefGate(null)}
        onConfirm={confirmChangeReference}
      />
      {editModal !== null && (
        <EditModal
          row={{ ...rows[editModal.idx] }}
          detergents={detergents}
          solvents={solvents}
          canEdit={editModal.mode === 'edit'}
          onClose={() => setEditModal(null)}
          onSave={saveFromModal}
          onAddDetergent={handleAddDetergent}
          onAddSolvent={handleAddSolvent}
        />
      )}

      {eSignModal !== null && (
        <ESignModal
          title={eSignModal.title}
          action={eSignModal.action}
          label={eSignModal.label}
          onClose={() => setESignModal(null)}
          onConfirm={handleESign}
        />
      )}

      {addDetModal !== null && (
        <AddMasterModal
          title="Add New Detergent"
          nameLabel="Detergent Name"
          namePlaceholder="e.g. Caustic Soda 2%"
          extraFields={[
            { key: 'concentration', label: 'Concentration', placeholder: 'e.g. 2% w/v' },
            { key: 'category',      label: 'Category',      placeholder: 'e.g. Alkaline' },
          ]}
          onClose={() => setAddDetModal(null)}
          onSaved={async (fields) => {
            const res = await detergentMasterAPI.create({ detergent_name: fields.name, concentration: fields.concentration, category: fields.category });
            const newDet = res.data.data;
            setDetergents(prev => [...prev, newDet].sort((a, b) => a.detergent_name.localeCompare(b.detergent_name)));
            if (addDetModal.callback) addDetModal.callback(newDet.detergent_name);
            toast.success('Detergent added.');
          }}
        />
      )}

      {addSolvModal !== null && (
        <AddMasterModal
          title="Add Rinsing Agent / Solvent"
          nameLabel="Agent Name"
          namePlaceholder="e.g. WFI, Purified Water"
          extraFields={[]}
          onClose={() => setAddSolvModal(null)}
          onSaved={async (fields) => {
            const res = await acceptanceCriteriaAPI.saveSolvent({ solvent_name: fields.name, plant_code: activePlant || '' });
            const newSolv = res.data.data;
            setSolvents(prev => [...prev, newSolv].sort((a, b) => (a.solvent_name || '').localeCompare(b.solvent_name || '')));
            if (addSolvModal.callback) addSolvModal.callback(newSolv.solvent_name);
            toast.success('Rinsing agent added.');
          }}
        />
      )}
    </div>
  );
};

export default CleaningConfig;
